/**
 * Notifications service — bell-icon backend.
 */

import { apiClient } from "@/services/apiClient";
import type {
  Notification,
  NotificationListResponse,
} from "@/types/notifications";

interface ListParams {
  unreadOnly?: boolean;
  limit?: number;
  /** When set, only notifications scoped to this client are returned.
   *  Omit to see all notifications for the account. */
  clientId?: string | null;
}

function buildQuery(params: ListParams): string {
  const sp = new URLSearchParams();
  if (params.unreadOnly) sp.set("unread_only", "true");
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.clientId) sp.set("client_id", params.clientId);
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

export const notificationsService = {
  async list(params: ListParams = {}): Promise<NotificationListResponse> {
    return apiClient.get<NotificationListResponse>(
      `/api/notifications${buildQuery(params)}`,
    );
  },

  async unreadCount(clientId?: string | null): Promise<number> {
    const qs = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
    const r = await apiClient.get<{ unreadCount: number }>(
      `/api/notifications/unread-count${qs}`,
    );
    return r.unreadCount;
  },

  async markRead(id: string): Promise<Notification> {
    return apiClient.post<Notification>(
      `/api/notifications/${encodeURIComponent(id)}/read`,
      {},
    );
  },

  async markAllRead(): Promise<{ markedRead: number }> {
    return apiClient.post<{ markedRead: number }>(
      `/api/notifications/read-all`,
      {},
    );
  },
};
